/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { NavigationHeader } from './components/common/NavigationHeader';
import { Footer } from './components/common/Footer';
import { Landing } from './pages/Landing';
import { SignIn } from './pages/SignIn';
import { AuthCallback } from './pages/AuthCallback';
import { AccessDenied } from './pages/AccessDenied';
import { ApiTestPage } from './pages/ApiTestPage';
import { CategoryList } from './pages/Categories/CategoryList';
import { CategoryAdd } from './pages/Categories/CategoryAdd';
import { CategoryEdit } from './pages/Categories/CategoryEdit';
import { ProductList } from './pages/Products/ProductList';
import { ProductAddEdit } from './pages/Products/ProductAddEdit';
import { ProductView } from './pages/Products/ProductView';
import { AdminDashboard } from './pages/Admin/AdminDashboard';
import { OrganizationList } from './pages/Admin/OrganizationList';
import { OrganizationAddEdit } from './pages/Admin/OrganizationAddEdit';
import { OrganizationAIConfig } from './pages/Admin/OrganizationAIConfig';
import { UserList } from './pages/Admin/UserList';
import { UserAddEdit } from './pages/Admin/UserAddEdit';
import { ActivityLog } from './pages/Admin/ActivityLog';
import { AIResponseHistory } from './pages/Admin/AIResponseHistory';
import { GlobalSchemaTemplates } from './pages/Admin/GlobalSchemaTemplates';
import { AIModels } from './pages/Admin/AIModels';
import { IntegrationSettings } from './pages/Settings/IntegrationSettings';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { statusApi } from './services/api';
import { useAuth } from './hooks/useAuth';

// Protected Route wrapper
function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { user, isAuthEnabled, isLoading } = useAuth();

  // Apex domain is marketing-only — no app access regardless of auth
  // Admin subdomain is allowed for admin routes only
  if (!isTenantSubdomain() && !isAdminSubdomain()) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  // If auth is disabled, allow access
  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  // If auth is enabled but user is not authenticated
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // If admin is required but user is not admin
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/products" replace />;
  }

  return <>{children}</>;
}

// Router-aware navigation components
function RouterHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const handleNavigate = (page: string) => {
    const routes: Record<string, string> = {
      'landing': '/',
      'signin': '/signin',
      'products': '/products',
      'categories': '/categories',
      'attributes': '/attributes',
      'settings': '/settings',
      'admin': '/admin',
      'api-test': '/api-test',
    };
    
    navigate(routes[page] || '/');
  };
  
  return <NavigationHeader onNavigate={handleNavigate} currentUser={user} />;
}

// Detect if we're on a tenant subdomain (e.g. koshinko.smartcatalog.app)
function isTenantSubdomain(): boolean {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  // tenant.smartcatalog.app = 3 parts, but exclude "admin" and "www"
  if (parts.length >= 3) {
    const subdomain = parts[0];
    return subdomain !== 'admin' && subdomain !== 'www';
  }
  return false;
}

// Detect if we're on the admin subdomain (admin.smartcatalog.app)
function isAdminSubdomain(): boolean {
  return window.location.hostname.split('.')[0] === 'admin';
}

// Page wrappers with navigation
function LandingWrapper() {
  const navigate = useNavigate();
  const { user, isAuthEnabled, isLoading } = useAuth();
  const isTenant = isTenantSubdomain();

  // On tenant subdomains, never show the landing page:
  // - Authenticated users go to /products
  // - Unauthenticated users go to /signin
  useEffect(() => {
    if (isTenant && !isLoading) {
      if (!isAuthEnabled || user) {
        navigate('/products', { replace: true });
      } else {
        navigate('/signin', { replace: true });
      }
    }
  }, [isTenant, user, isAuthEnabled, isLoading, navigate]);

  // Show loading while checking auth on tenant subdomains
  if (isTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Apex domain: always show the public landing page
  return <Landing />;
}

function SignInWrapper() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  
  // If already authenticated, redirect to landing page
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, isLoading, navigate]);
  
  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }
  
  return <SignIn onAuthSuccess={() => {
    navigate('/products');
  }} />;
}

function ProductListWrapper() {
  const navigate = useNavigate();
  return <ProductList onNavigate={(page, id) => {
    if (page === 'product-view' && id) {
      navigate(`/products/${id}`);
    } else if (page === 'product-add-edit' && id) {
      navigate(`/products/edit/${id}`);
    } else if (page === 'product-add-edit') {
      navigate('/products/add');
    }
  }} />;
}

function ProductViewWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <ProductView productId={id || ''} onNavigate={(page, entityId) => {
    if (page === 'products') {
      navigate('/products');
    } else if (page === 'product-add-edit' && entityId) {
      navigate(`/products/edit/${entityId}`);
    }
  }} />;
}

function ProductAddEditWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <ProductAddEdit productId={id} onNavigate={() => navigate('/products')} />;
}

function CategoryListWrapper() {
  const navigate = useNavigate();
  return <CategoryList onNavigate={(page, id) => {
    if (page === 'category-edit' && id) {
      navigate(`/categories/edit/${id}`);
    } else if (page === 'category-add') {
      navigate('/categories/add');
    }
  }} />;
}

function CategoryAddWrapper() {
  const navigate = useNavigate();
  return <CategoryAdd onNavigate={() => navigate('/categories')} />;
}

function CategoryEditWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <CategoryEdit categoryId={id || ''} onNavigate={() => navigate('/categories')} />;
}

function AdminDashboardWrapper() {
  const navigate = useNavigate();
  return <AdminDashboard onNavigate={(page) => {
    if (page === 'admin-organizations') {
      navigate('/admin/organizations');
    } else if (page === 'admin-users') {
      navigate('/admin/users');
    } else if (page === 'admin-activity') {
      navigate('/admin/activity');
    } else if (page === 'admin-ai-responses') {
      navigate('/admin/ai-responses');
    } else if (page === 'admin-schemas') {
      navigate('/admin/schemas');
    } else if (page === 'admin-ai-models') {
      navigate('/admin/ai-models');
    }
  }} />;
}

function OrganizationListWrapper() {
  const navigate = useNavigate();
  return <OrganizationList onNavigate={(page, id) => {
    if (page === 'admin-organization-edit' && id) {
      navigate(`/admin/organizations/edit/${id}`);
    } else if (page === 'admin-organization-add') {
      navigate('/admin/organizations/add');
    } else if (page === 'admin-organization-ai-config' && id) {
      navigate(`/admin/organizations/ai-config/${id}`);
    }
  }} />;
}

function OrganizationAddEditWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <OrganizationAddEdit organizationId={id} onNavigate={() => navigate('/admin/organizations')} />;
}

function OrganizationAIConfigWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <OrganizationAIConfig organizationId={id!} onNavigate={() => navigate('/admin/organizations')} />;
}

function UserListWrapper() {
  const navigate = useNavigate();
  return <UserList onNavigate={(page, id) => {
    if (page === 'admin-user-edit' && id) {
      navigate(`/admin/users/edit/${id}`);
    } else if (page === 'admin-user-add') {
      navigate('/admin/users/add');
    } else if (page === 'admin') {
      navigate('/admin');
    }
  }} />;
}

function UserAddEditWrapper() {
  const navigate = useNavigate();
  const { id } = useParams();
  return <UserAddEdit userId={id} onNavigate={(page) => {
    if (page === 'admin-users') {
      navigate('/admin/users');
    } else if (page === 'admin') {
      navigate('/admin');
    }
  }} />;
}

function ActivityLogWrapper() {
  const navigate = useNavigate();
  return <ActivityLog onNavigate={(page) => {
    if (page === 'admin') {
      navigate('/admin');
    }
  }} />;
}

function GlobalSchemaTemplatesWrapper() {
  const navigate = useNavigate();
  return <GlobalSchemaTemplates onNavigate={(page, id) => {
    if (page === 'admin') {
      navigate('/admin');
    }
    // TODO: Add routes for template editing when modal/dialogs are implemented
  }} />;
}

function AIModelsWrapper() {
  const navigate = useNavigate();
  return <AIModels onNavigate={(page) => {
    if (page === 'admin') {
      navigate('/admin');
    }
  }} />;
}

function SettingsWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return <IntegrationSettings
    onNavigate={(page) => navigate(page === 'products' ? '/products' : '/')}
    squareConnected={params.get('square_connected') === 'true'}
    squareError={params.get('square_error') || undefined}
  />;
}

function AppContent() {
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false);
  const { user, isAuthEnabled, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-redirect admin subdomain users to /admin (skip auth-related paths)
  useEffect(() => {
    const path = window.location.pathname;
    const authPaths = ['/signin', '/auth/callback', '/access-denied', '/privacy', '/terms'];
    const isAuthPath = authPaths.some(p => path.startsWith(p));
    if (isAdminSubdomain() && !path.startsWith('/admin') && !isAuthPath) {
      navigate('/admin', { replace: true });
    }
  }, []);

  // Check environment status on app load
  useEffect(() => {
    checkEnvironmentStatus();
  }, []);

  const checkEnvironmentStatus = async () => {
    try {
      const response = await statusApi.getStatus();
      if (response.success && response.data) {
        
        // Determine development mode based on environment
        const isDev = response.data.environment === 'development' || 
                     response.data.environment === 'dev' ||
                     response.data.environment === 'staging' ||
                     import.meta.env.DEV;
        
        setIsDevelopmentMode(isDev);
      } else {
        setIsDevelopmentMode(import.meta.env.DEV);
      }
    } catch (error) {
      console.error('Failed to check environment status:', error);
      setIsDevelopmentMode(import.meta.env.DEV);
    }
  };

  // Don't show header on signin page, auth callback page, or when we're still checking auth on protected routes
  const isProtectedRoute = location.pathname.startsWith('/products') ||
                          location.pathname.startsWith('/admin') || location.pathname.startsWith('/categories') ||
                          location.pathname.startsWith('/settings');
  const isLandingPage = location.pathname === '/';
  const showHeader = !isLandingPage &&
                    location.pathname !== '/signin' &&
                    location.pathname !== '/auth/callback' &&
                    location.pathname !== '/privacy' &&
                    location.pathname !== '/terms' &&
                    !(isProtectedRoute && isLoading) &&
                    !(isProtectedRoute && isAuthEnabled && !user);
  const showFooter = !isLandingPage;

  return (
    <div className="app-container">
      <ErrorBoundary>
        {showHeader && <RouterHeader />}
        
        <main className="main-content">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingWrapper />} />
            <Route path="/signin" element={<SignInWrapper />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* Protected routes */}
            <Route path="/products" element={
              <ProtectedRoute>
                <ProductListWrapper />
              </ProtectedRoute>
            } />
            <Route path="/products/add" element={
              <ProtectedRoute>
                <ProductAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/products/edit/:id" element={
              <ProtectedRoute>
                <ProductAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/products/:id" element={
              <ProtectedRoute>
                <ProductViewWrapper />
              </ProtectedRoute>
            } />
            
            <Route path="/categories" element={
              <ProtectedRoute>
                <CategoryListWrapper />
              </ProtectedRoute>
            } />
            <Route path="/categories/add" element={
              <ProtectedRoute>
                <CategoryAddWrapper />
              </ProtectedRoute>
            } />
            <Route path="/categories/edit/:id" element={
              <ProtectedRoute>
                <CategoryEditWrapper />
              </ProtectedRoute>
            } />

            {/* Settings route */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsWrapper />
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <AdminDashboardWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/organizations" element={
              <ProtectedRoute requireAdmin>
                <OrganizationListWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/organizations/add" element={
              <ProtectedRoute requireAdmin>
                <OrganizationAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/organizations/edit/:id" element={
              <ProtectedRoute requireAdmin>
                <OrganizationAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/organizations/ai-config/:id" element={
              <ProtectedRoute requireAdmin>
                <OrganizationAIConfigWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireAdmin>
                <UserListWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/add" element={
              <ProtectedRoute requireAdmin>
                <UserAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/users/edit/:id" element={
              <ProtectedRoute requireAdmin>
                <UserAddEditWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/activity" element={
              <ProtectedRoute requireAdmin>
                <ActivityLogWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-responses" element={
              <ProtectedRoute requireAdmin>
                <AIResponseHistory />
              </ProtectedRoute>
            } />
            <Route path="/admin/schemas" element={
              <ProtectedRoute requireAdmin>
                <GlobalSchemaTemplatesWrapper />
              </ProtectedRoute>
            } />
            <Route path="/admin/ai-models" element={
              <ProtectedRoute requireAdmin>
                <AIModelsWrapper />
              </ProtectedRoute>
            } />

            {/* Development route */}
            {isDevelopmentMode && (
              <Route path="/api-test" element={<ApiTestPage />} />
            )}
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        {showFooter && <Footer />}
      </ErrorBoundary>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;